import React, { FC, useEffect, useMemo } from 'react';
import { PromoTvChannelProgram } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ReadonlySignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';
import { createPictureFactory, Picture, PictureData } from '@funtech-int/yango-shared';

import PlayIcon from '@/shared/assets/MoviePlay';
import * as CardKit from '@/shared/components/CardKit';
import { Fader } from '@/shared/components/Fader/Fader';
import { Button } from '@/shared/components/Button/Button';
import { useCurrentProgram } from '@/shared/hooks/useCurrentProgram';

import { useI18n } from '../../hooks/useI18n';
import { transformTvProgramTypeNameMap } from '../../lib/utils/transformTvProgramTypeNameMap';

import styles from './ChannelPromoBlock.module.css';

const logoFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '48x48',
            '2x': '96x96',
        },
        src: '48x48',
    },
});

const backgroundFactory = createPictureFactory({
    fallback: {
        src: '1747x982',
    },
});

interface ChannelPromoBlockAnalytics {
    itemImpressionShowed(this: void): void;
    itemImpressionNavigated(
        this: void,
        to: 'payment' | 'player' | 'movie_card' | 'channel_player'
    ): void;
}

export interface ChannelPromoBlockProps {
    promoBlock: PromoTvChannelProgram;
    timeSignal: ReadonlySignal<number>;
    analytics?: ChannelPromoBlockAnalytics;
    onWatchClick: (contentId: string) => void;
    onFinish: VoidFunction;
}

export const ChannelPromoBlock: FC<ChannelPromoBlockProps> = ({
    promoBlock,
    timeSignal,
    analytics,
    onWatchClick,
    onFinish,
}) => {
    const now = useSignal(timeSignal);
    const i18n = useI18n();

    const controller = useMemo(() => {
        const searchParams = new URLSearchParams({
            openedChannelId: promoBlock.channel.contentId,
        });

        return {
            searchParams,
            onWatchClick: () => {
                analytics?.itemImpressionNavigated('channel_player');
                onWatchClick(promoBlock.channel.contentId);
            },
        };
    }, [promoBlock, analytics, onWatchClick]);

    const { img: logo } = logoFactory({
        fallbackUrl: promoBlock.channel.logo?.toString(),
    });

    const { program, time, announce } = useCurrentProgram(promoBlock.channel, now);

    let background: PictureData | undefined;

    if (program) {
        background = backgroundFactory({
            fallbackUrl: program.image?.toString(),
        });
    }

    useEffect(() => {
        analytics?.itemImpressionShowed();
    }, [analytics]);

    useEffect(() => {
        if (now > promoBlock.finishTime) {
            onFinish();
        }
    }, [onFinish, now, promoBlock.finishTime]);

    return (
        <div className={styles.root}>
            <Fader contentKey={program?.id ?? ''} className={styles.fader}>
                {background?.img.src ? (
                    <Picture
                        src={background.img.src}
                        className={styles.background}
                        alt={program?.title}
                    />
                ) : null}
            </Fader>
            <div className={styles.fade} />
            <div className={styles.container}>
                <div className={styles.content}>
                    <div className={styles.channel}>
                        <Picture
                            srcSet={logo.srcSet}
                            src={logo.src}
                            className={styles.logo}
                            data-tid="AdaptiveImage"
                        />
                        <div className={styles.title}>{promoBlock.channel.title}</div>
                    </div>
                    {program ? (
                        <div className={styles.program}>
                            <div className={styles.programTitle}>{program.title}</div>
                            <div className={styles.meta}>
                                <span className={styles.onAir}>
                                    {i18n('channels_on_the_air_text')}
                                </span>
                                <span className={styles.text}>
                                    {transformTvProgramTypeNameMap(program.typeName, i18n)}
                                </span>
                                <span className={styles.text}>
                                    {program.ageRestriction?.toString()}
                                </span>
                                <span className={styles.text}>{time}</span>
                                {!announce ? (
                                    <span className={styles.progress}>
                                        <CardKit.Progress
                                            value={program.elapsed(now)}
                                            maxValue={program.duration}
                                        />
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
                <Button
                    name="Watch"
                    variant="accent"
                    size={60}
                    startIcon={<PlayIcon fill="#fff" />}
                    onClick={controller.onWatchClick}
                >
                    {i18n('channels_watch_text')}
                </Button>
            </div>
        </div>
    );
};
